// Iter 220 — Module 2: Progressive Daily Auto-Betting Strategy panel.
//
// Surfaced as a dedicated tab under /account.  Layered to keep the
// scary "fire real money" action behind two explicit toggles
// (`enabled` + `auto_place`) so a misclick never costs the user $50.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Activity, AlertTriangle, CheckCircle2, FlaskConical, Flame,
  PlayCircle, RefreshCw, Save, ShieldCheck, Sparkles, TrendingUp,
  XCircle,
} from "lucide-react";

const STAKE_MODES = [
  { value: "fixed",            label: "Fixed amount (USD)" },
  { value: "percent_balance",  label: "% of balance" },
  { value: "multiplier",       label: "Multiplier of last payout" },
];

const ALL_MARKETS = [
  { id: "double_chance", label: "Double Chance" },
  { id: "halftime",      label: "Halftime (HT O/U 0.5 etc.)" },
];

const STATUS_TONE = {
  placed:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  failed:  "bg-rose-500/15   text-rose-300   border-rose-500/40",
  skipped: "bg-amber-500/15  text-amber-300  border-amber-500/40",
  dry_run: "bg-sky-500/15    text-sky-300    border-sky-500/40",
};

const VERDICT_TONE = {
  won:     "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  lost:    "bg-rose-500/15    text-rose-300    border-rose-500/40",
  void:    "bg-slate-500/15   text-slate-300   border-slate-500/40",
  pending: "bg-amber-500/15   text-amber-300   border-amber-500/40",
};

export default function StrategyPanel() {
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [review, setReview] = useState(null);

  const dirty = useMemo(() => {
    if (!config || !draft) return false;
    return JSON.stringify(config) !== JSON.stringify(draft);
  }, [config, draft]);

  const fetchAll = async () => {
    try {
      const [cfg, runsRes, reviewRes] = await Promise.all([
        axios.get(`${API}/strategy/config`),
        axios.get(`${API}/strategy/runs?limit=15`),
        axios.get(`${API}/strategy/review`),
      ]);
      setConfig(cfg.data);
      setDraft(cfg.data);
      setRuns(runsRes.data.runs || []);
      setReview(reviewRes.data);
    } catch (err) {
      toast.error("Failed to load strategy data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const patch = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const toggleMarket = (m) => {
    setDraft((d) => {
      const cur = d.markets || [];
      const next = cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m];
      return { ...d, markets: next };
    });
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      // Strip server-managed fields
      delete payload.review_day_of_week;
      const { data } = await axios.put(`${API}/strategy/config`, payload);
      setConfig(data);
      setDraft(data);
      toast.success("Strategy settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const runPreview = async () => {
    setPreviewing(true); setPreview(null);
    try {
      const { data } = await axios.post(`${API}/strategy/preview`);
      setPreview(data);
      const nLegs = (data.planned_selections || []).length;
      if (nLegs === 0) {
        toast.info(data.reason ? `No parlay built: ${data.reason}` : "No legs available");
      } else {
        toast.success(`Preview built: ${nLegs}-leg parlay @ $${data.planned_stake?.toFixed(2)}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const executeNow = async () => {
    if (!config?.enabled) {
      toast.error("Enable the strategy first");
      return;
    }
    if (!config?.auto_place) {
      const ok = window.confirm(
        "Auto-place is OFF — clicking Execute Now will still NOT place a real bet. "
        + "Flip 'auto_place' to ON in settings first to fire live."
      );
      if (!ok) return;
    } else {
      const ok = window.confirm(
        "⚠️ This will place a REAL MONEY parlay on your linked Betway account. "
        + "Stake is calculated from your settings (max $" + (config.max_daily_stake || 50) + ").\n\n"
        + "Continue?"
      );
      if (!ok) return;
    }
    setExecuting(true);
    try {
      const { data } = await axios.post(`${API}/strategy/execute`);
      if (data.status === "placed") {
        toast.success("🎯 Parlay placed on Betway!");
      } else if (data.status === "skipped") {
        toast.info(`Skipped: ${data.reason}`);
      } else if (data.status === "failed") {
        toast.error(`Failed: ${data.reason || data.message}`);
      } else {
        toast.info(`Status: ${data.status}`);
      }
      // Refresh runs + review after a run
      const [runsRes, reviewRes] = await Promise.all([
        axios.get(`${API}/strategy/runs?limit=15`),
        axios.get(`${API}/strategy/review`),
      ]);
      setRuns(runsRes.data.runs || []);
      setReview(reviewRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Execute failed");
    } finally { setExecuting(false); }
  };

  const settleNow = async () => {
    try {
      const { data } = await axios.post(`${API}/strategy/settle?lookback_days=14`);
      toast.success(`Settled ${data.settled} / scanned ${data.scanned}`);
      const reviewRes = await axios.get(`${API}/strategy/review`);
      setReview(reviewRes.data);
      const runsRes = await axios.get(`${API}/strategy/runs?limit=15`);
      setRuns(runsRes.data.runs || []);
    } catch (err) {
      toast.error("Settle failed");
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card/40 px-5 py-8 text-center text-muted-foreground" data-testid="strategy-loading">
        Loading strategy…
      </div>
    );
  }
  if (!draft) return null;

  return (
    <div className="space-y-5" data-testid="strategy-panel">
      {/* ── Master enable + auto_place bar ─────────────────────────── */}
      <div
        className="rounded-xl border border-border bg-card/50 p-5 flex flex-col sm:flex-row sm:items-center gap-4"
        data-testid="strategy-master-bar"
      >
        <div className="flex items-center gap-3 flex-1">
          <Flame className={`w-6 h-6 ${draft.enabled ? "text-amber-400" : "text-muted-foreground"}`} />
          <div>
            <div className="font-display text-lg font-bold tracking-tight">Daily Double Strategy</div>
            <div className="text-xs text-muted-foreground">
              Auto-build & place a parlay from today&apos;s top Double Chance + Halftime picks
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="strategy-enabled-toggle">
            <Switch
              checked={!!draft.enabled}
              onCheckedChange={(v) => patch("enabled", v)}
              data-testid="strategy-enabled-switch"
            />
            <span className="text-xs font-bold uppercase tracking-[0.12em]">Enabled</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="strategy-auto-place-toggle">
            <Switch
              checked={!!draft.auto_place}
              onCheckedChange={(v) => patch("auto_place", v)}
              data-testid="strategy-auto-place-switch"
            />
            <span className="text-xs font-bold uppercase tracking-[0.12em] text-rose-400">
              Auto-Place (Real $)
            </span>
          </label>
        </div>
      </div>

      {/* ── Config form ────────────────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card/40 p-5 space-y-5" data-testid="strategy-config-form">
        <div className="font-display text-base font-bold flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400" /> Configuration
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {/* Stake */}
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Stake mode
            </Label>
            <Select value={draft.stake_mode} onValueChange={(v) => patch("stake_mode", v)}>
              <SelectTrigger data-testid="strategy-stake-mode-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAKE_MODES.map((m) => (
                  <SelectItem key={m.value} value={m.value} data-testid={`stake-mode-${m.value}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.5"
                min="0"
                value={draft.stake_value ?? ""}
                onChange={(e) => patch("stake_value", parseFloat(e.target.value) || 0)}
                data-testid="strategy-stake-value-input"
                className="flex-1"
              />
              <div className="text-[11px] text-muted-foreground w-32">
                {draft.stake_mode === "percent_balance"
                  ? "Decimal (0.10 = 10 %)"
                  : draft.stake_mode === "multiplier"
                  ? "× last payout"
                  : "USD per parlay"}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Max daily stake (USD)
            </Label>
            <Input
              type="number"
              step="1"
              min="1"
              max="10000"
              value={draft.max_daily_stake ?? ""}
              onChange={(e) => patch("max_daily_stake", parseFloat(e.target.value) || 0)}
              data-testid="strategy-max-daily-input"
            />
            <div className="text-[11px] text-muted-foreground">Hard ceiling — capped no matter what stake_mode resolves to.</div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Min confidence per leg ({draft.min_confidence?.toFixed(0)} %)
            </Label>
            <Input
              type="range"
              min="50"
              max="99"
              step="1"
              value={draft.min_confidence ?? 75}
              onChange={(e) => patch("min_confidence", parseFloat(e.target.value))}
              data-testid="strategy-min-confidence-slider"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Max legs ({draft.max_legs})
            </Label>
            <Input
              type="range"
              min="1"
              max="8"
              step="1"
              value={draft.max_legs ?? 3}
              onChange={(e) => patch("max_legs", parseInt(e.target.value, 10))}
              data-testid="strategy-max-legs-slider"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Stop-loss (consecutive losses)
            </Label>
            <Input
              type="number"
              min="0"
              max="20"
              step="1"
              value={draft.stop_loss_threshold ?? 3}
              onChange={(e) => patch("stop_loss_threshold", parseInt(e.target.value, 10) || 0)}
              data-testid="strategy-stop-loss-input"
            />
            <div className="text-[11px] text-muted-foreground">Set 0 to disable.  After N losses in a row the engine pauses for the day.</div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Markets to consider
            </Label>
            <div className="flex flex-wrap gap-2">
              {ALL_MARKETS.map((m) => {
                const on = (draft.markets || []).includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleMarket(m.id)}
                    data-testid={`strategy-market-${m.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.1em] border transition ${
                      on
                        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                        : "bg-card/30 text-muted-foreground border-border hover:border-foreground/40"
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            onClick={saveConfig}
            disabled={!dirty || saving}
            data-testid="strategy-save-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Button>
          <Button
            variant="outline"
            onClick={runPreview}
            disabled={previewing}
            data-testid="strategy-preview-btn"
          >
            <FlaskConical className="w-4 h-4 mr-1.5" />
            {previewing ? "Building…" : "Preview today's parlay"}
          </Button>
          <Button
            onClick={executeNow}
            disabled={executing || !draft.enabled}
            data-testid="strategy-execute-btn"
            className="bg-rose-500 hover:bg-rose-400 text-white font-bold"
          >
            <PlayCircle className="w-4 h-4 mr-1.5" />
            {executing ? "Running…" : "Execute Now"}
          </Button>
          <Button
            variant="ghost"
            onClick={settleNow}
            data-testid="strategy-settle-btn"
            title="Backfill won/lost verdicts for placed bets"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Refresh ROI
          </Button>
        </div>
      </div>

      {/* ── Preview card ─────────────────────────────────────────── */}
      {preview && (
        <div className="rounded-xl border border-sky-500/40 bg-sky-500/5 p-4 space-y-3" data-testid="strategy-preview-result">
          <div className="flex items-center gap-2 text-sm font-bold">
            <FlaskConical className="w-4 h-4 text-sky-400" />
            Preview · {preview.status} · {preview.reason}
          </div>
          {preview.planned_stake != null && (
            <div className="text-xs text-muted-foreground">
              Stake: <span className="text-foreground font-bold">${preview.planned_stake?.toFixed(2)}</span>{" "}
              · Legs: <span className="text-foreground font-bold">{(preview.planned_selections || []).length}</span>
            </div>
          )}
          <div className="space-y-1">
            {(preview.planned_selections || []).map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-xs rounded-md border border-border/60 bg-background/40 px-3 py-2">
                <div className="flex-1 min-w-0 truncate">
                  <span className="text-muted-foreground">{s.league}</span>
                  <span className="mx-2 opacity-50">·</span>
                  <span className="font-bold">{s.home_team} vs {s.away_team}</span>
                </div>
                <Badge variant="outline" className="border-sky-500/40 text-sky-300 text-[10px]">
                  {s.market_label}: {s.pick}
                </Badge>
                <span className="text-xs text-muted-foreground w-12 text-right">{s.confidence?.toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Weekly ROI card ─────────────────────────────────────── */}
      {review && (
        <div className="rounded-xl border border-border bg-card/40 p-5" data-testid="strategy-review-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <div className="font-display text-base font-bold tracking-tight">Weekly Review (last 7 days)</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ReviewTile label="Runs" value={review.total_runs} />
            <ReviewTile label="Placed" value={review.placed} tone="emerald" />
            <ReviewTile label="Won" value={review.won} tone="emerald" />
            <ReviewTile label="Lost" value={review.lost} tone="rose" />
            <ReviewTile label="Pending" value={review.pending} tone="amber" />
            <ReviewTile label="Staked" value={`$${review.total_staked?.toFixed(2) ?? "0.00"}`} />
            <ReviewTile label="Returned" value={`$${review.total_returned?.toFixed(2) ?? "0.00"}`} tone="emerald" />
            <ReviewTile
              label="ROI"
              value={`${review.roi_pct > 0 ? "+" : ""}${review.roi_pct?.toFixed(1) ?? "0.0"}%`}
              tone={review.roi_pct > 0 ? "emerald" : review.roi_pct < 0 ? "rose" : "slate"}
            />
          </div>
        </div>
      )}

      {/* ── Run history ──────────────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card/40 p-5" data-testid="strategy-runs-card">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-sky-400" />
          <div className="font-display text-base font-bold tracking-tight">Recent runs</div>
          <Badge variant="outline" className="text-[10px]">{runs.length}</Badge>
        </div>
        {runs.length === 0 ? (
          <div className="text-xs text-muted-foreground">No runs yet — click Preview or Execute to seed.</div>
        ) : (
          <div className="space-y-2">
            {runs.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between gap-2 text-xs rounded-md border border-border/60 bg-background/40 px-3 py-2"
                data-testid={`strategy-run-row-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{r.created_at?.slice(0, 19).replace("T", " ")}</div>
                  <div className="text-muted-foreground truncate">{r.reason || r.message || "—"}</div>
                </div>
                <Badge
                  variant="outline"
                  className={`text-[10px] ${STATUS_TONE[r.status] || ""}`}
                >
                  {r.status}
                </Badge>
                {r.verdict && r.verdict !== "pending" && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${VERDICT_TONE[r.verdict] || ""}`}
                  >
                    {r.verdict === "won" ? <CheckCircle2 className="w-3 h-3 mr-0.5" /> : r.verdict === "lost" ? <XCircle className="w-3 h-3 mr-0.5" /> : null}
                    {r.verdict}
                  </Badge>
                )}
                {r.stake && (
                  <span className="text-muted-foreground w-14 text-right tabular-nums">${r.stake.toFixed(2)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!draft.enabled && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 flex items-start gap-2" data-testid="strategy-disabled-banner">
          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
          <div className="text-xs">
            <div className="font-bold">Strategy is currently disabled.</div>
            <div className="text-muted-foreground">Toggle <code className="px-1 rounded bg-card/60">Enabled</code> above to allow Preview / Execute. <code className="px-1 rounded bg-card/60">Auto-Place</code> must ALSO be on for real money to be wagered — Preview always stays in dry-run mode.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewTile({ label, value, tone = "slate" }) {
  const toneClasses = {
    emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
    rose:    "border-rose-500/40   bg-rose-500/5   text-rose-300",
    amber:   "border-amber-500/40  bg-amber-500/5  text-amber-300",
    slate:   "border-border bg-background/30 text-foreground",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneClasses[tone] || toneClasses.slate}`}>
      <div className="text-[10px] uppercase tracking-[0.12em] opacity-70">{label}</div>
      <div className="font-bold text-base tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
